import React, {
  useState
} from "react";

import {
  Eye
} from "lucide-react";

import TraceabilityDetailDrawer
from "./TraceabilityDetailDrawer";


// =============================================================================
// COMPONENT
// =============================================================================

export default function TraceabilityGrid({

  rows = [],

  loading = false

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [selectedRow, setSelectedRow] =
    useState(null);


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>

        Loading traceability data...

      </div>
    );
  }


  // ===========================================================================
  // EMPTY
  // ===========================================================================
  if (!rows || rows.length === 0) {

    return (

      <div style={styles.emptyState}>

        No traceability records found

      </div>
    );
  }


  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================
  return (

    <>

      <div style={styles.container}>


        {/* ================================================================ */}
        {/* HEADER */}
        {/* ================================================================ */}
        <div style={styles.headerRow}>


          <div style={styles.colProject}>
            Project
          </div>

          <div style={styles.colMaterial}>
            Material
          </div>

          <div style={styles.colQty}>
            Planned
          </div>

          <div style={styles.colQty}>
            Reserved
          </div>

          <div style={styles.colQty}>
            Dispatch
          </div>

          <div style={styles.colQty}>
            Received
          </div>

          <div style={styles.colQty}>
            Reconciled
          </div>

          <div style={styles.colQty}>
            Pending
          </div>

          <div style={styles.colStatus}>
            Status
          </div>

          <div style={styles.colValidation}>
            Validation
          </div>

          <div style={styles.colAction}>
            Actions
          </div>

        </div>


        {/* ================================================================ */}
        {/* BODY */}
        {/* ================================================================ */}
        <div style={styles.body}>


          {

            rows.map((row) => (

              <div
                key={row.id}
                style={styles.dataRow}
              >


                {/* -------------------------------------------------------- */}
                {/* PROJECT */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colProject}>


                  <div style={styles.primaryText}>

                    {row.project_name}

                  </div>


                  <div style={styles.secondaryText}>

                    {row.phase_name}

                  </div>


                  <div style={styles.secondaryText}>

                    {row.task_name}

                  </div>

                </div>


                {/* -------------------------------------------------------- */}
                {/* MATERIAL */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colMaterial}>


                  <div style={styles.primaryText}>

                    {row.material_code}

                  </div>


                  <div style={styles.secondaryText}>

                    {row.material_name}

                  </div>

                </div>


                {/* -------------------------------------------------------- */}
                {/* PLANNED */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>

                  {row.planned_qty || 0}

                </div>


                {/* -------------------------------------------------------- */}
                {/* RESERVED */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>

                  {row.reserved_qty || 0}

                </div>


                {/* -------------------------------------------------------- */}
                {/* DISPATCHED */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>

                  {row.dispatched_qty || 0}

                </div>


                {/* -------------------------------------------------------- */}
                {/* RECEIVED */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>

                  {row.received_qty || 0}

                </div>


                {/* -------------------------------------------------------- */}
                {/* RECONCILED */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>

                  {row.reconciled_qty || 0}

                </div>


                {/* -------------------------------------------------------- */}
                {/* PENDING */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colQty}>


                  <div style={styles.pendingWrap}>


                    <div>

                      D:
                      {" "}

                      {row.pending_dispatch_qty || 0}

                    </div>


                    <div>

                      R:
                      {" "}

                      {row.pending_receipt_qty || 0}

                    </div>


                    <div>

                      RC:
                      {" "}

                      {
                        row.pending_reconciliation_qty || 0
                      }

                    </div>

                  </div>

                </div>


                {/* -------------------------------------------------------- */}
                {/* STATUS */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colStatus}>


                  <span style={
                    getStatusStyle(
                      row.receipt_status
                    )
                  }>

                    {

                      row.receipt_status ||

                      row.dispatch_status ||

                      "PENDING"
                    }

                  </span>

                </div>


                {/* -------------------------------------------------------- */}
                {/* VALIDATION */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colValidation}>


                  <span style={

                    row.validation_status ===
                    "OK"

                      ? styles.okBadge

                      : styles.errorBadge
                  }>

                    {row.validation_status}

                  </span>

                </div>


                {/* -------------------------------------------------------- */}
                {/* ACTION */}
                {/* -------------------------------------------------------- */}
                <div style={styles.colAction}>


                  <button
                    style={styles.actionBtn}
                    title="View Detail"
                    onClick={() =>
                      setSelectedRow(row)
                    }
                  >

                    <Eye size={16} />

                  </button>

                </div>

              </div>
            ))
          }

        </div>

      </div>


      {/* ================================================================ */}
      {/* DETAIL DRAWER */}
      {/* ================================================================ */}
      <TraceabilityDetailDrawer

        open={!!selectedRow}

        row={selectedRow}

        onClose={() =>
          setSelectedRow(null)
        }
      />

    </>
  );
}


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "RECONCILED":

      return styles.statusGreen;

    case "RECEIVED":

      return styles.statusBlue;

    case "IN_TRANSIT":

      return styles.statusOrange;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {

    background: "#ffffff",

    borderRadius: 14,

    overflow: "hidden",

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  headerRow: {

    display: "flex",

    background: "#f3f4f6",

    borderBottom:
      "1px solid #e5e7eb",

    padding: "14px 16px",

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  body: {

    display: "flex",

    flexDirection: "column"
  },

  dataRow: {

    display: "flex",

    padding: "16px",

    borderBottom:
      "1px solid #f3f4f6",

    alignItems: "center"
  },

  primaryText: {

    fontWeight: 600,

    fontSize: 14,

    color: "#111827"
  },

  secondaryText: {

    fontSize: 12,

    color: "#6b7280",

    marginTop: 4
  },

  pendingWrap: {

    fontSize: 12,

    lineHeight: "18px"
  },

  colProject: {

    width: 220,

    paddingRight: 12
  },

  colMaterial: {

    width: 220,

    paddingRight: 12
  },

  colQty: {

    width: 90,

    textAlign: "center"
  },

  colStatus: {

    width: 130,

    display: "flex",

    justifyContent: "center"
  },

  colValidation: {

    width: 120,

    display: "flex",

    justifyContent: "center"
  },

  colAction: {

    width: 90,

    display: "flex",

    justifyContent: "center"
  },

  actionBtn: {

    width: 34,

    height: 34,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    background: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer"
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 600
  },

  statusBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 600
  },

  statusOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 600
  },

  statusGray: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 600
  },

  okBadge: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  errorBadge: {

    background: "#fee2e2",

    color: "#991b1b",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  loader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center"
  },

  emptyState: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center",

    color: "#6b7280"
  }
};